import { IPlanificationPhaseProfileWorkDay } from '@/entity/planification/planification-phase-profile-work-day';
import { IPlanificationProfileScheduler } from '@/entity/planification/planification-profile-scheduler';
import { moduleApiGraph } from '@t/module-api-graph';
import { NU } from '@t/type';
import { addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';

export enum ERecapCalendar {
    InteractiveSelection = 0,
    SchedulerShifting = 1,
    SchedulerDeleting = 2,
}

export enum ViewMode {
    Year = 1,
    Month = 2,
    Week = 3
}

export interface IRetrieveKey {
    contractLineId: number;
    employeeId: string;
    profileId: number;
    indexInPhase: number | null;
}

export interface IPhaseProfileByKey {
    key: string;
    value: IRetrieveKey;
}

export interface TimelineEvent {
    event: {
        altKey: boolean | null;
        type: 'click' | 'mouseover' | 'pointerdown';
        timeStamp: number
        target: {
            className: string;
            parentElement: HTMLElement,
        } | HTMLElement,
        srcElement: HTMLElement
    };
    time: any;
    group: string;
    item: string;
    what: 'item' | 'background' | 'axis' | 'group-label';
    snappedTime: any;
}

export interface IGroupBo {
    id: string;
    title: string;
    content: any;
    treeLevel: number;
    nestedGroups: string[] | null;
    showNested: boolean;
}

export interface IEmployeeBo {
    id: string;
    group: string | null;
    content: string;
    start: string;
    end: string;
    type: 'range' | 'background';
    style?: string;
    className?: string;
}

export interface INewDayOff {
    contractLineId: number;
    employeeId: string;
    profileId: number;
    indexInPhase: number;
    date: string;
    datePartId: number;
    isDeleted: boolean;
}

const month = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre'
];

const monthsShort = [
    'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Déc'
];

const week = { dow: 0, doy: 6 };
const weekdaysShort = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const weekdaysMin = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const weekdays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export const newEmployeBo = (item: IPlanificationProfileScheduler): IEmployeeBo => {
    return {
        id: '',
        group: generateKey(item.contractLineId, item.indexInPhase, item.employeeId, item.profileId),
        content: '',
        start: '',
        end: '',
        type: 'range',
        className: ''
    } as IEmployeeBo;
};

export const timelineOption = (start: Date, end: Date) => {
    return {
        // locale: 'en', ne fonctionne pas vis2js bug..
        showCurrentTime: true,
        showTooltips: true,
        stack: false,
        align: 'top',
        moveable: true,
        horizontalScroll: true,
        verticalScroll: true,
        orientation: {
            axis: 'top',
            item: 'bottom'
        },
        editable: false,
        zoomMin: 1000 * 60 * 60 * 24,
        zoomMax: 31536000000 * 10 /* 1 an x5 */,
        zoomKey: 'ctrlKey',
        start: start,
        maxHeight: '50vh',
        end: end,
        margin: {
            item: {
                horizontal: -1,
                vertical: 0
            },
            axis: 0
        },
        format: {
            minorLabels: {
                day: 'DD'
            },
            majorLabels: {
                year: 'YYYY'
            }
        },
    };
};

const hasClass = (element: EventTarget | null | undefined, className: string): boolean => {
    return element instanceof HTMLElement && element.classList.contains(className);
};

export const isPhaseCollapse = (props: TimelineEvent): boolean => {
    return hasClass(props.event.target.parentElement, 'phase-action');
};

export const isShiftCheckBox = (props: TimelineEvent): boolean => {
    return hasClass(props.event.srcElement as HTMLElement, 'checkbox-shift');
};

export const isDeleteCheckBox = (props: TimelineEvent): boolean => {
    return hasClass(props.event.srcElement as HTMLElement, 'checkbox-delete');
};

export const isOnClickPhase = (props: TimelineEvent): boolean => {
    return hasClass(props.event.srcElement as HTMLElement, 'on-click-phase');
};

export const isOnClickProfile = (props: TimelineEvent): boolean => {
    return hasClass(props.event.srcElement as HTMLElement, 'on-click-with-profil');
};

export const dateToString = (date: NU<Date | string>, currentFormat: string, numberDaysAdd?: number): string => {
    if (!currentFormat) {
        currentFormat = 'dd/MM/yyyy';
    }
    if (date) {
        let currentDate;
        if (typeof date === 'string') {
            currentDate = new Date(date);
        } else if (typeof new Date() === typeof date) {
            currentDate = date;
        } else {
            return '';
        }
        if (numberDaysAdd && (numberDaysAdd > 0 || numberDaysAdd < 0)) {
            currentDate = addDays(currentDate, numberDaysAdd);
        }
        return format(currentDate, currentFormat, { locale: fr });
    }
    return '-';
};

export const displayHeader = (loading: boolean) => {
    const timelineHeader = document.querySelector('#timeline-header');
    if (timelineHeader && !loading) {
        timelineHeader.classList.remove('hide');
    }
};

export const setHeader = () => {
    const header = document.querySelector('.vis-timeline.vis-bottom');
    if (header) {
        const elemHeader = document.createElement('div');
        elemHeader.id = 'timeline-header';
        // elemHeader.className = 'hide';
        [
            { c: 'phase-header', l: ['Phase'] },
            { c: 'date-header', l: ['Début', 'Fin'] },
            { c: 'budget-header', l: ['Prévisionnel', 'Budget'] },
        ].forEach((elem) => {
            const box = document.createElement('div');
            elem.l.forEach((l) => {
                const subBox = document.createElement('div');
                subBox.textContent = l;
                subBox.className = l.toLowerCase();
                box.appendChild(subBox);
            });
            box.className = elem.c;
            elemHeader.appendChild(box);
        });
        header!.insertBefore(elemHeader, document.querySelector('.vis-panel.vis-background'));
    }
};

export const setLocalTranslation = (scheduler: any) => {
    scheduler.timeline.components
        .filter((r: any) => typeof r.defaultOptions.moment === 'function')
        .forEach((e: any) =>
            e.defaultOptions.moment.defineLocale('fr', {
                months: month,
                monthsShort: monthsShort,
                weekdays: weekdays,
                week: week,
                weekdaysMin: weekdaysMin,
                weekdaysShort: weekdaysShort
            })
        );
};
export interface MgtPersonLight {
    displayName: string;
}

export interface MgtPersonDic {
    [key: string]: MgtPersonLight;
}

export const getUserByIds = async (ids: string[], dic: MgtPersonDic, isDisplayRichez: boolean): Promise<MgtPersonDic> => {
    ids.forEach(async (id) => {
        if (!dic[id]) {
            const user: MgtPersonLight = await moduleApiGraph.Client.api(`/users/${id}?$select=displayName`)
                .get();

            let displayName = user?.displayName ? user.displayName : '';
            if (!isDisplayRichez) {
                displayName = displayName.replace('Richez_Associés', '').replace('Richez_Associes', '');
            }
            dic[id] = { displayName };
        }
    });
    return dic;
};

export const generateKey = (
    contractLineId: number,
    indexInPhase: number,
    employeeId: string,
    profileId: number,
) => {
    return `${contractLineId}_${indexInPhase}_${employeeId}_${profileId}`;
};

export const generateWorkoutKey = (
    type: 'full' | 'half',
    contractLineId: number,
    indexInPhase: number,
    employeeId: string,
    profileId: number,
    keyDate: string,
    datePartId: number
) => {
    return `${type}_${contractLineId}_${indexInPhase}_${employeeId}_${profileId}_${keyDate}_${datePartId}`;
};

export const retrieveValues = (key: string): IRetrieveKey | null => {
    const values = key?.split('_') ?? [];
    return values.length === 4
        ? {
            contractLineId: parseInt(values[0]),
            indexInPhase: !isNaN(parseInt(values[1])) ? parseInt(values[1]) : null,
            employeeId: values[2].toLowerCase(),
            profileId: parseInt(values[3]),
        }
        : null;
};

export const setHierarchicalOrder = (phases: { contractLineId: number; type: string; obj: IGroupBo }[]) => {
    let resultOrdered: IGroupBo[] = [];
    phases
        .filter((p) => p.type === 'phase')
        .forEach((p) => {
            resultOrdered.push(p.obj);
            const child = phases
                .filter((c) => c.type === 'user' && c.contractLineId === p.contractLineId)
                .map((m, index) => {
                    return { ...m.obj, index: index };
                });
            resultOrdered = resultOrdered.concat(child);
        });
    return resultOrdered;
};

export const setClassName = (classBase: string, items: IPlanificationPhaseProfileWorkDay[]): string => {
    let suffix: string = '';
    if (items.some((s) => s.isShifted)) {
        suffix = '-shifted';
    } else if (items.some((s) => s.isDeleted)) {
        suffix = '-deleted';
    }
    return `${classBase}${suffix}`;
};

const matchItem = (sp: IPlanificationProfileScheduler, item: IPlanificationProfileScheduler) =>
    sp.profileId === item.profileId &&
    sp.employeeId === item.employeeId &&
    sp.indexInPhase === item.indexInPhase;

export const workoutByState = (
    item: IPlanificationProfileScheduler,
    phaseProfilShifts: IPlanificationProfileScheduler[],
    phaseProfilDeleted: IPlanificationProfileScheduler[],
): IPlanificationPhaseProfileWorkDay[] => {
    const deletedWorkDay = phaseProfilDeleted.find((sp) => matchItem(sp, item))?.planificationPhaseProfileWorkDays ?? [];
    const shiftedWorkDay = phaseProfilShifts.find((sp) => matchItem(sp, item))?.planificationPhaseProfileWorkDays ?? [];
    
    return deletedWorkDay.length > 0 ? deletedWorkDay 
        : shiftedWorkDay.length > 0 ? shiftedWorkDay
            : item.planificationPhaseProfileWorkDays;
};
